import React, {useEffect, useState, createContext, useContext, useCallback} from 'react';
import {TruckOutline} from 'antd-mobile-icons';
import {NavBar} from "antd-mobile";
import {Outlet, useNavigate} from "react-router-dom";
import {t} from "../../../i18n";
import PickUpMenuDrawer from "../components/PickUpMenuDrawer";
import pickedUpDataService from "../../../services/PickedUpDataService";
import {PickUpItem, PickUpPathNavigationTabBar} from "../components/PickUpPathNavigationTabBar";
import PickUpMenuIconWithBadge from "../components/PickUpMenuIconWithBadge";
import { MapModeIcon } from 'assets/icons';
import { PickingUpPath } from 'enums/PickUpNavigatePathEnum';
import { getNavBarContainerStyle, getBottomNavBarContainerStyle } from "../../../utils/deviceUtils";

interface PickUpLayoutContextType {
    refreshFailedCount: () => Promise<void>;
    failedCount: number;
}

export const PickUpLayoutContext = createContext<PickUpLayoutContextType>({
    refreshFailedCount: async () => {},
    failedCount: 0
});

// 自定义Hook，方便子组件使用
export const usePickUpLayout = () => useContext(PickUpLayoutContext);

type PickUpLayoutProps = {
    children?: React.ReactNode;
    title: string;
    /**
     * 固定在导航栏正下方、不随内容滚动的横幅槽位（当前用于「仅查看今日任务」只读态提示）。
     * 放这里而不是塞进 children：只读态下横幅是唯一的解释和唯一的开工入口，滚走了司机就找不到。
     */
    banner?: React.ReactNode;
}

const pickUpItems: PickUpItem[] = [
    {
        path: "/pickup/picking-up",
        icon: <TruckOutline/>,
        label: `${t('pages.itemPickUp')}`
    }
]

const PickUpLayout: React.FC<PickUpLayoutProps> = ({children, title, banner}) => {
    const [menuVisible, setMenuVisible] = useState(false);
    const [failedCount, setFailedCount] = useState<number>(0);
    const navigate = useNavigate();

    useEffect(() => {
        handleFailedCount();
    }, [title])

    const handleFailedCount = async () => {
        try {
            // 使用await正确处理异步调用
            const count = await pickedUpDataService.getPickedUpDataCount();
            setFailedCount(count);
        } catch (error) {
            console.error('Failed to fetch orders:', error);
        }
    };

    const handleOpenMenu = () => {
        setMenuVisible(true);
    };

    const handleCloseMenu = () => {
        setMenuVisible(false);
    };

    const refreshFailedCount = useCallback(async () => {
        await handleFailedCount();
    }, []);

    const contextValue = {
        refreshFailedCount,
        failedCount
    };
    
    const handleMapModeClick = () => {
        sessionStorage.setItem('mapModeEntrySource', 'delivering-list');
        navigate(PickingUpPath.PICKUP_PICKINGUP_MAP);
    };

    return (
        <PickUpLayoutContext.Provider value={contextValue}>
            <div className="flex flex-col h-screen bg-gray-50 overflow-hidden">
                {/* 用户菜单抽屉 */}
                <PickUpMenuDrawer
                    visible={menuVisible}
                    onClose={handleCloseMenu}
                    failedCount={failedCount}
                />

                {/* 顶部导航栏 - 固定 */}
                <div {...getNavBarContainerStyle('flex-none sticky top-0 z-10 bg-white shadow-sm')}>
                    <NavBar
                        backIcon={<PickUpMenuIconWithBadge count={failedCount}/>}
                        onBack={handleOpenMenu}
                        right={<div className="flex items-center justify-end w-full" onClick={handleMapModeClick}>
                               <MapModeIcon width={24} height={24} />
                           </div>}
                    >
                    </NavBar>
                </div>

                {/* 只读态横幅 - 固定，不随内容滚动 */}
                {banner && <div className="flex-none">{banner}</div>}

                {/* 中间内容区域 - 弹性滚动 */}
                <div  id="pickup-main-container" className="flex-1 overflow-y-auto">
                    {children || <Outlet/>}
                </div>

                {/* 底部导航 - 固定 */}
                <div className="flex-none sticky bottom-0 z-10 bg-white border-t border-gray-200">
                    <PickUpPathNavigationTabBar items={pickUpItems}/>
                </div>
            </div>
        </PickUpLayoutContext.Provider>
    );
};

export default PickUpLayout;